import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout } from '../../layouts/AuthLayout';
import { AppCard } from '../../components/ui/AppCard';
import { SectionTitle } from '../../components/ui/SectionTitle';
import { TextField } from '../../components/ui/TextField';
import { Button } from '../../components/ui/Button';
import { Toast } from '../../components/ui/Toast';
import { useStore } from '../../store/StoreContext';
import { resendLoginCode } from '../../api/authApi';
import { SocialLoginButtons } from './SocialLoginButtons';
import { PasskeyButton } from './PasskeyButton';
import { useFeature } from '../../lib/features';

export function LoginScreen() {
  const { login, loginWithPasskey, verifyTwoFactor, verifyAuthenticator, errorMessage, setErrorMessage } = useStore();
  const resetOn = useFeature('auth.password_reset');
  const signupOn = useFeature('auth.signup');
  const socialOn = useFeature('auth.social_login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Set once /login comes back with twoFactorRequired or
  // authenticatorRequired - the screen then swaps the email/password form
  // for the code-entry step. `kind` picks which second call to make.
  const [challengeId, setChallengeId] = useState('');
  const [challengeKind, setChallengeKind] = useState<'email' | 'authenticator'>('email');
  const [code, setCode] = useState('');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return setError("Email can't be empty.");
    if (!password.trim()) return setError("Password can't be empty.");
    setLoading(true);
    try {
      const result = await login(email.trim(), password);
      if (result && result.challengeId) {
        setChallengeId(result.challengeId);
        setChallengeKind(result.kind);
        setNotice(
          result.kind === 'authenticator'
            ? 'Enter the code from your authenticator app.'
            : `We sent a sign-in code to ${result.email}.`,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to log in.');
    } finally {
      setLoading(false);
    }
  };

  const signInPasskey = async () => {
    setError('');
    setPasskeyLoading(true);
    try {
      await loginWithPasskey();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey sign-in failed.');
    } finally {
      setPasskeyLoading(false);
    }
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return setError('Enter the code.');
    setLoading(true);
    try {
      if (challengeKind === 'authenticator') {
        await verifyAuthenticator(challengeId, code.trim());
      } else {
        await verifyTwoFactor(challengeId, code.trim());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code was not accepted.');
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError('');
    try {
      const message = await resendLoginCode(challengeId);
      setNotice(message);
      setCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend the code.');
    }
  };

  const startOver = () => {
    setChallengeId('');
    setChallengeKind('email');
    setCode('');
    setPassword('');
    setNotice('');
    setError('');
  };

  return (
    <AuthLayout>
      <SectionTitle title={challengeId ? 'Enter your code' : 'Welcome back'} />
      <AppCard>
        {challengeId ? (
          <form onSubmit={submitCode} className="flex flex-col gap-4">
            {notice && <p className="text-sm text-slate-600 dark:text-slate-300">{notice}</p>}
            <TextField
              label={challengeKind === 'authenticator' ? 'Authenticator code' : 'Sign-in code'}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <Button type="submit" full loading={loading}>
              Verify
            </Button>
            <div className="flex items-center justify-between text-sm">
              {challengeKind === 'authenticator' ? (
                <span className="text-xs text-black/50 dark:text-white/50">Lost your device? Use a backup code instead.</span>
              ) : (
                <button type="button" onClick={resend} className="text-blue-600 dark:text-blue-400 hover:underline">
                  Resend code
                </button>
              )}
              <button type="button" onClick={startOver} className="text-blue-600 dark:text-blue-400 hover:underline">
                Use a different account
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4">
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button type="submit" full loading={loading}>
              Login
            </Button>
            <div className="flex items-center justify-between text-sm">
              {resetOn ? (
                <Link to="/reset-password" className="text-blue-600 dark:text-blue-400 hover:underline">
                  Forgot your password?
                </Link>
              ) : (
                <span />
              )}
              {signupOn && (
                <Link to="/register" className="text-blue-600 dark:text-blue-400 hover:underline">
                  Sign up
                </Link>
              )}
            </div>
          </form>
        )}
      </AppCard>
      {!challengeId && (
        <AppCard>
          <div className="flex flex-col gap-3">
            <PasskeyButton onClick={signInPasskey} loading={passkeyLoading} disabled={loading}>
              Sign in with a passkey
            </PasskeyButton>
            {socialOn && <SocialLoginButtons />}
          </div>
        </AppCard>
      )}
      {error && <Toast message={error} onDismiss={() => setError('')} />}
      {/* Surfaced when StoreContext's 401 handler auto-signed the user out
          mid-session (see its setUnauthorizedHandler wiring). */}
      {!error && errorMessage && <Toast message={errorMessage} onDismiss={() => setErrorMessage(null)} />}
    </AuthLayout>
  );
}
